<template>
  <div>
    <div class="calendar-month">
      <div class="calendar-month-header">
        <CalendarDateIndicator :selected-date="selectedDate" class="calendar-month-header-selected-month" />
        <CalendarDateSelector :current-date="today" :selected-date="selectedDate" @dateSelected="selectDate" />
      </div>

      <v-progress-linear v-if="loading" indeterminate color="#48B38B"></v-progress-linear>

      <CalendarWeekdays />

      <ol class="days-grid">
        <CalendarMonthDayItem
          v-for="day in days"
          :key="day.date"
          :day="day"
          :is-today="day.date === today"
          :appointments="appointmentsOfDay(day.date)"
          :warning-day="warning_day"
          :services="servicesOfDay(day.date)"
          @open-day-appointments="openDayAppointments"
        />
      </ol>

      <DialogAppointment :day="dialogAppointmentDay" @newAppointment="newAppointment" @editAppointment="editAppointment" />
    </div>

    <div class="schedule-footer">
      <small class="pt-2">*Obs: O fundo dos dias representa a quantidade de carros no patio com base na data prevista para finalizar a R.S.</small>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
// eslint-disable-next-line import/no-extraneous-dependencies
import weekday from 'dayjs/plugin/weekday';
// eslint-disable-next-line import/no-extraneous-dependencies
import weekOfYear from 'dayjs/plugin/weekOfYear';

// eslint-disable-next-line import/no-extraneous-dependencies
import 'dayjs/locale/pt-br';

// eslint-disable-next-line import/extensions
import CalendarMonthDayItem from './CalendarMonthDayItem.vue';
import CalendarDateIndicator from './CalendarDateIndicator';
import CalendarDateSelector from './CalendarDateSelector';
import CalendarWeekdays from './CalendarWeekdays';
import DialogAppointment from './DialogAppointment';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);

export default {
  components: {
    CalendarMonthDayItem,
    CalendarDateIndicator,
    CalendarDateSelector,
    CalendarWeekdays,
    DialogAppointment
  },
  data() {
    return {
      loading: false,
      appointments: null,
      service_orders: [],
      selectedDate: dayjs().locale('pt-br'),
      dialogAppointmentDay: {
        status: false
      },
      warning_day: {
        full: 12,
        almost_full: 6
      }
    };
  },
  mounted() {
    this.getAppointments();
  },
  computed: {
    days() {
      return [...this.previousMonthDays, ...this.currentMonthDays, ...this.nextMonthDays];
    },
    firstDayInArray() {
      return this.days[0];
    },
    lastDayInArray() {
      return this.days[this.days.length - 1];
    },
    today() {
      return dayjs().format('YYYY-MM-DD');
    },
    month() {
      return Number(this.selectedDate.format('M'));
    },
    year() {
      return Number(this.selectedDate.format('YYYY'));
    },
    numberOfDaysInMonth() {
      return dayjs(this.selectedDate).daysInMonth();
    },
    currentMonthDays() {
      return [...Array(this.numberOfDaysInMonth)].map((day, index) => ({
        date: dayjs(`${this.year}-${this.month}-${index + 1}`).format('YYYY-MM-DD'),
        isCurrentMonth: true
      }));
    },
    previousMonthDays() {
      const firstDayOfTheMonthWeekday = this.getWeekday(this.currentMonthDays[0].date);
      const previousMonth = dayjs(`${this.year}-${this.month}-01`).subtract(1, 'month');

      // Cover first day of the month being sunday (firstDayOfTheMonthWeekday === 0)
      const visibleNumberOfDaysFromPreviousMonth = firstDayOfTheMonthWeekday ? firstDayOfTheMonthWeekday - 1 : 6;

      const previousMonthLastMondayDayOfMonth = dayjs(this.currentMonthDays[0].date).subtract(visibleNumberOfDaysFromPreviousMonth, 'day').date();

      return [...Array(visibleNumberOfDaysFromPreviousMonth)].map((day, index) => ({
        date: dayjs(`${previousMonth.year()}-${previousMonth.month() + 1}-${previousMonthLastMondayDayOfMonth + index}`).format('YYYY-MM-DD'),
        isCurrentMonth: false
      }));
    },
    nextMonthDays() {
      const lastDayOfTheMonthWeekday = this.getWeekday(`${this.year}-${this.month}-${this.currentMonthDays.length}`);

      const nextMonth = dayjs(`${this.year}-${this.month}-01`).add(1, 'month');

      const visibleNumberOfDaysFromNextMonth = lastDayOfTheMonthWeekday ? 7 - lastDayOfTheMonthWeekday : lastDayOfTheMonthWeekday;

      return [...Array(visibleNumberOfDaysFromNextMonth)].map((day, index) => ({
        date: dayjs(`${nextMonth.year()}-${nextMonth.month() + 1}-${index + 1}`).format('YYYY-MM-DD'),
        isCurrentMonth: false
      }));
    }
  },
  methods: {
    addAppointment(appointment) {
      this.appointments.rows.push(appointment);
      this.dialogAppointmentDay.status = false;
    },
    updateAppointment() {
      this.dialogAppointmentDay.status = false;

      this.getAppointments();
    },
    newAppointment(day) {
      this.$emit('newAppointment', day);
    },
    editAppointment(appointment) {
      this.$emit('editAppointment', appointment);
    },
    openDayAppointments(day) {
      this.dialogAppointmentDay = { ...day, status: true, services: this.servicesOfDay(day.date) };
    },
    appointmentsOfDay(date) {
      return this.appointments && this.appointments.rows
        ? this.appointments.rows.filter((appointment) => {
            if (appointment.forecast) {
              const forecastDate = dayjs(appointment.forecast).format('YYYY-MM-DDT00:00:00.000Z');
              return forecastDate.substring(0, 10) === date;
            }
            return true;
          })
        : [];
    },
    getWeekday(date) {
      return dayjs(date).weekday();
    },
    selectDate(newSelectedDate) {
      this.selectedDate = newSelectedDate;
      this.getAppointments();
    },
    getAppointments() {
      const self = this;
      self.loading = true;
      self.$http
        .get(
          `/scheduling/${self.selectedDate.format('MM')}/${self.selectedDate.format('YYYY')}/appointments?initial_date=${
            self.firstDayInArray.date
          }&end_date=${self.lastDayInArray.date}`
        )
        .then((response) => {
          self.loading = false;
          self.appointments = response.data;
          self.getServiceOrders();
        })
        .catch(() => {
          self.loading = false;
        });
    },
    getServiceOrders() {
      const self = this;
      self.loading = true;
      self.$http
        .get(`/scheduling/service_orders`, {
          params: {
            initial_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            end_date: self.selectedDate.endOf('year').format('YYYY-MM-DD')
          }
        })
        .then((response) => {
          self.loading = false;
          self.service_orders = response.data;
        })
        .catch(() => {
          self.loading = false;
        });
    },
    servicesOfDay(targetDate) {
      let serviceOrders = [];
      if (dayjs(targetDate).isAfter(dayjs(), 'day') || targetDate.includes(dayjs().format('YYYY-MM-DD'))) {
        serviceOrders =
          this.service_orders && this.service_orders.length > 0
            ? this.service_orders.filter((service) => {
                const forecastDateDjs = dayjs(service.forecast_date.substring(0, 10));
                const targetDateDjs = dayjs(targetDate);
                return forecastDateDjs.isAfter(targetDateDjs, 'day') || targetDate.includes(service.forecast_date.substring(0, 10));
              })
            : [];
      }
      return serviceOrders;
    }
  }
};
</script>

<style lang="scss">
body {
  --grey-100: #e4e9f0;
  --grey-200: #cfd7e3;
  --grey-300: #b5c0cd;
  --grey-800: #3e4e63;
  --grid-gap: 1px;
  --day-label-size: 20px;
}

.calendar-month {
  position: relative;
  background-color: var(--grey-200);
  border: solid 1px var(--grey-300);

  .calendar-month-header {
    display: flex;
    justify-content: space-between;
    padding: 20px 20px;
    background-color: #eeeeee;
  }

  ol,
  li {
    padding: 0 !important;
    margin: 0 !important;
    list-style: none !important;
  }
}

.day-of-week {
  color: rgba(0, 0, 0, 0.6) !important;
  font-size: 18px;
  background-color: #fff;
  padding-bottom: 5px;
  padding-top: 10px;
}

.day-of-week,
.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.day-of-week > * {
  text-align: center !important;
  padding-right: 5px;
}

.days-grid {
  height: 100%;
  position: relative;
  grid-column-gap: var(--grid-gap);
  grid-row-gap: var(--grid-gap);
  border-top: solid 1px rgb(197, 197, 197);
  padding: 0px !important;
  margin: 0px !important;
}

.schedule-footer {
  padding: 10px;
}
</style>
